import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import ProviderFieldsForm from './provider-fields-form';
import SchemaFieldsList from './schema-fields-list';
import FieldEditDialog from './field-edit-dialog';
import AppDialog from '../../../components/AppDialog';
import ShadcnTabs, { ShadcnTabPanel } from '../../../components/ShadcnTabs';

import {
  useGetPaymentProvider,
  useRenameProvider,
  useAddProviderField,
  useUpdateProviderField,
  useDeleteProviderField,
} from '../services';
import { TFieldSchema, TFieldScope } from '../schemas/payment-provider-schema';

type ProviderEditDialogProps = {
  open: boolean;
  providerId: string | null;
  onClose: () => void;
  onSave: (globalFields: Record<string, string>) => void;
  isSaving?: boolean;
};

type FieldDialogState =
  | { open: false }
  | { open: true; mode: 'add'; scope: TFieldScope; field?: undefined }
  | { open: true; mode: 'edit'; scope: TFieldScope; field: TFieldSchema };

const ProviderEditDialog = ({
  open,
  providerId,
  onClose,
  onSave,
  isSaving,
}: ProviderEditDialogProps) => {
  const [tab, setTab] = useState<TFieldScope>('global');
  const [values, setValues] = useState<Record<string, string>>({});
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [fieldDialog, setFieldDialog] = useState<FieldDialogState>({ open: false });
  const [fieldToDelete, setFieldToDelete] = useState<TFieldSchema | null>(null);

  const { data: provider, isLoading, refetch } = useGetPaymentProvider(providerId || '');
  const renameMutation = useRenameProvider();
  const addFieldMutation = useAddProviderField();
  const updateFieldMutation = useUpdateProviderField();
  const deleteFieldMutation = useDeleteProviderField();

  useEffect(() => {
    if (open && providerId) {
      refetch();
      setTab('global');
    }
  }, [open, providerId, refetch]);

  useEffect(() => {
    if (provider) {
      setValues({ ...(provider.globalFields || {}) });
      setRenameValue(provider.displayName || provider.providerId);
    }
  }, [provider]);

  const allFields = useMemo(() => provider?.schema?.fields || [], [provider]);
  const counts = useMemo(
    () => ({
      global: allFields.filter((f) => (f.scope ?? 'global') === 'global').length,
      client: allFields.filter((f) => f.scope === 'client').length,
      device: allFields.filter((f) => f.scope === 'device').length,
    }),
    [allFields],
  );

  const globalFieldsSchema = useMemo(
    () => allFields.filter((f) => (f.scope ?? 'global') === 'global'),
    [allFields],
  );

  const handleSave = () => {
    onSave(values);
  };

  const handleRenameSubmit = async () => {
    if (!providerId || !renameValue.trim()) return;
    await renameMutation.mutateAsync({
      providerId,
      payload: { displayName: renameValue.trim() },
    });
    setIsRenaming(false);
    refetch();
  };

  const handleFieldDialogSave = async (payload: {
    id: string;
    type: TFieldSchema['type'];
    scope: TFieldScope;
    required: boolean;
    displayOrder: number;
    label?: string;
  }) => {
    if (!providerId || !fieldDialog.open) return;
    if (fieldDialog.mode === 'add') {
      await addFieldMutation.mutateAsync({
        providerId,
        payload: {
          id: payload.id,
          type: payload.type,
          scope: payload.scope,
          required: payload.required,
          displayOrder: payload.displayOrder,
          label: payload.label,
        },
      });
    } else {
      await updateFieldMutation.mutateAsync({
        providerId,
        fieldId: payload.id,
        payload: {
          label: payload.label,
          required: payload.required,
          displayOrder: payload.displayOrder,
        },
      });
    }
    setFieldDialog({ open: false });
    refetch();
  };

  const handleDeleteFieldConfirm = async () => {
    if (!providerId || !fieldToDelete) return;
    await deleteFieldMutation.mutateAsync({ providerId, fieldId: fieldToDelete.id });
    setFieldToDelete(null);
    refetch();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={styles.title}>
          {provider ? (
            isRenaming ? (
              <Box sx={styles.renameRow}>
                <TextField
                  size="small"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  disabled={renameMutation.isPending}
                  autoFocus
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Save name">
                  <IconButton
                    size="small"
                    onClick={handleRenameSubmit}
                    disabled={renameMutation.isPending || !renameValue.trim()}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsRenaming(false);
                      setRenameValue(provider.displayName || provider.providerId);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={styles.titleRow}>
                <Box>
                  <Typography sx={styles.titleText}>
                    {provider.displayName || provider.providerId}
                  </Typography>
                  <Typography sx={styles.subtitle}>
                    Provider ID: {provider.providerId}
                  </Typography>
                </Box>
                <Tooltip title="Rename provider">
                  <IconButton size="small" onClick={() => setIsRenaming(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          ) : (
            'Edit Provider'
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={styles.content}>
            {isLoading || !provider ? (
              <Box sx={styles.loaderBox}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  <ShadcnTabs
                    value={tab}
                    onChange={(v) => setTab(v as TFieldScope)}
                    tabs={[
                      { value: 'global', label: 'Global', count: counts.global },
                      { value: 'client', label: 'Client', count: counts.client },
                      { value: 'device', label: 'Device', count: counts.device },
                    ]}
                  />
                </Box>

                <ShadcnTabPanel value="global" current={tab}>
                  <Typography sx={styles.scopeBlurb}>
                    Foodo&apos;s shared credentials with the acquirer. These values are applied to
                    every restaurant.
                  </Typography>

                  <Box mb={2}>
                    <SchemaFieldsList
                      fields={allFields}
                      scope="global"
                      manageable
                      onAdd={() =>
                        setFieldDialog({ open: true, mode: 'add', scope: 'global' })
                      }
                      onEdit={(f) =>
                        setFieldDialog({ open: true, mode: 'edit', scope: 'global', field: f })
                      }
                      onDelete={(f) => setFieldToDelete(f)}
                    />
                  </Box>

                  {globalFieldsSchema.length > 0 && (
                    <Box>
                      <Typography sx={styles.formHeader}>Set Global Values</Typography>
                      <ProviderFieldsForm
                        schema={globalFieldsSchema}
                        values={values}
                        encryptedKeys={provider.globalFieldsEncryptedKeys || []}
                        onChange={setValues}
                        disabled={isSaving}
                      />
                    </Box>
                  )}
                </ShadcnTabPanel>

                <ShadcnTabPanel value="client" current={tab}>
                  <Typography sx={styles.scopeBlurb}>
                    Per-restaurant credentials. Values are set inside each restaurant&apos;s payment
                    providers page.
                  </Typography>
                  <SchemaFieldsList
                    fields={allFields}
                    scope="client"
                    manageable
                    onAdd={() =>
                      setFieldDialog({ open: true, mode: 'add', scope: 'client' })
                    }
                    onEdit={(f) =>
                      setFieldDialog({ open: true, mode: 'edit', scope: 'client', field: f })
                    }
                    onDelete={(f) => setFieldToDelete(f)}
                  />
                </ShadcnTabPanel>

                <ShadcnTabPanel value="device" current={tab}>
                  <Typography sx={styles.scopeBlurb}>
                    Picked on-device from the acquirer&apos;s terminal list. Not stored centrally —
                    only the schema is configured here.
                  </Typography>
                  <SchemaFieldsList
                    fields={allFields}
                    scope="device"
                    manageable
                    onAdd={() =>
                      setFieldDialog({ open: true, mode: 'add', scope: 'device' })
                    }
                    onEdit={(f) =>
                      setFieldDialog({ open: true, mode: 'edit', scope: 'device', field: f })
                    }
                    onDelete={(f) => setFieldToDelete(f)}
                  />
                </ShadcnTabPanel>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={styles.actions}>
          <Button sx={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
            Close
          </Button>
          {tab === 'global' && globalFieldsSchema.length > 0 && (
            <Button
              sx={styles.saveBtn}
              onClick={handleSave}
              disabled={isSaving || isLoading || !provider}
            >
              {isSaving ? 'Saving…' : 'Save Global Values'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <FieldEditDialog
        open={fieldDialog.open}
        mode={fieldDialog.open ? fieldDialog.mode : 'add'}
        scope={fieldDialog.open ? fieldDialog.scope : 'global'}
        initial={fieldDialog.open && fieldDialog.mode === 'edit' ? fieldDialog.field : undefined}
        onClose={() => setFieldDialog({ open: false })}
        onSave={handleFieldDialogSave}
        isSaving={addFieldMutation.isPending || updateFieldMutation.isPending}
      />

      <AppDialog
        open={!!fieldToDelete}
        onClose={() => setFieldToDelete(null)}
        title="Delete field?"
        content={`Field "${fieldToDelete?.id}" will be removed from this provider's schema. Cannot delete if any value (global or client) is stored.`}
        firstBtnContent="Cancel"
        secondBtnContent="Delete"
        firstBtnClickHandler={() => setFieldToDelete(null)}
        secondBtnClickHandler={handleDeleteFieldConfirm}
        options={{ keepMounted: false, btnLoading: deleteFieldMutation.isPending }}
      />
    </>
  );
};

export default ProviderEditDialog;

const styles = {
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
  },
  titleText: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
  },
  renameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#64748b',
    mt: 0.25,
  },
  scopeBlurb: {
    fontFamily: 'GilroyMedium',
    fontSize: 12,
    color: '#64748b',
    mb: 1.5,
  },
  formHeader: {
    fontFamily: 'GilroyBold',
    fontSize: 13,
    color: '#0f172a',
    mb: 1.5,
    mt: 1.5,
  },
  content: {
    pt: 1,
    minHeight: 220,
  },
  loaderBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  actions: {
    px: 3,
    py: 2,
  },
  cancelBtn: {
    textTransform: 'capitalize' as const,
    color: '#000',
    fontFamily: 'GilroySemiBold',
    border: '1px solid #000',
    borderRadius: '8px',
    px: 2.5,
  },
  saveBtn: {
    textTransform: 'capitalize' as const,
    backgroundColor: '#000',
    color: '#FFF',
    fontFamily: 'GilroySemiBold',
    borderRadius: '8px',
    px: 2.5,
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
